import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Forms from '@/styled/common/forms'
import { ErrorEnum } from '@/utils/announcements'
import { useAuth, useSignIn } from "@clerk/clerk-react"
import Loading from './Loading'
import { parseErrors } from "@/helpers/authentication"
import { useOAuth } from "@/hooks/useOAuth"
import { Button } from "@/components/common/Button"
import { capitalize } from "lodash"
import { useClerkEnvironment } from "@/hooks/useClerkEnvironment"
import { useClerkSocialLogins } from "@/hooks/useClerkSocialLogins"
import { useAnnouncementsContext } from '@/contexts/AnnouncementsContext'
import { get } from '@/helpers'

export const Authentication = () => {
    const { removeAnnouncement } = useAnnouncementsContext()

    const { maintenanceMode } = useClerkEnvironment()
    const socialLogins = useClerkSocialLogins()

    const emailRef = useRef<HTMLInputElement>(null)
    const [state, setState] = useState({
        user: {
            username: '',
            email: '',
            password: '',
            passwordConfirmation: '',
            access_key: ''
        },
        loading: false,
        showPassword: false,
        error: {} as any
    })
    const { isLoaded, signIn, setActive = () => undefined } = useSignIn()
    const { signOut } = useAuth()
    const { startOAuthFlow } = useOAuth()

    useEffect(() => {
        removeAnnouncement?.(ErrorEnum.HOST_CONNECTION_ERROR)
    }, [removeAnnouncement])

    function displayFormErrors(err: any) {
        return parseErrors(err.errors)
    }

    const trySignIn = async (formDataUser: any) => {
        const completeSignIn = await signIn!.create({
            identifier: formDataUser.email,
            password: formDataUser.password
        })

        if (completeSignIn.status === 'complete') {
            await setActive({ session: completeSignIn.createdSessionId })
        } else {
            setState(prevState => ({
                ...prevState,
                loading: false
            }))
        }
    }

    const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!isLoaded) {
            return
        }

        const formDataUser = Object.fromEntries(new FormData(e.target as HTMLFormElement)) as any

        if (!formDataUser.email || !formDataUser.password) {
            setState(prevState => ({
                ...prevState,
                error: {
                    fieldErrors: {
                        email: formDataUser.email ? undefined : "Email is required",
                        password: formDataUser.password ? undefined : "Password is required",
                    }
                }
            }))
            return
        }

        setState(prevState => ({
            ...prevState,
            loading: true
        }))

        try {
            await trySignIn(formDataUser)
        } catch (err: any) {
            if (err.clerkError) {
                for (const error of err.errors) {
                    if (error.code === 'session_exists') {
                        void signOut()
                        await trySignIn(formDataUser)
                    }
                }
            }

            const formEl = e.target as HTMLFormElement
            formEl.elements.namedItem('password')
            ;(formEl.elements as any).password.value = ''
            ;(formDataUser.email ? (formEl.elements as any).password : emailRef).focus()
            setState(prevState => ({
                ...prevState,
                loading: false
            }))

            setState(prevState => ({
                ...prevState,
                error: displayFormErrors(err)
            }))
        }
    }

    const signInWith = async (provider: any) => {
        try {
            return await startOAuthFlow({ provider })
        } catch (err) {
            displayFormErrors(err)
        }
    }

    const socialButtons = useMemo(
        () =>
            socialLogins.map((provider: any) => (
                <Button className="!whitespace-nowrap flex flex-row !max-w-full !w-full gap-2 !m-0 !p-2 !text-nowrap !font-normal justify-center align-middle items-center"
                        disabled={!isLoaded && state.loading}
                        key={provider.strategy}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault()
                            void signInWith(provider)
                        }}>
                    {
                        provider.logo_url && <img src={provider.logo_url} className="h-4 w-4" alt={provider.name} />
                    }{' '}
                    {capitalize(provider.name)}
                </Button>
            )),
        [isLoaded, state.loading, socialLogins]
    )

    const emailHasError = get(state.error, 'fieldErrors.email')
    const passwordHasError = get(state.error, 'fieldErrors.password')
    const globalErrors = get(state.error, 'globalErrors')

    if (maintenanceMode) {
        return <h1>Currently in Maintenance mode</h1>
    }

    return state.loading ?
        <Loading message={'Logging in...'} /> :
        (
            <Forms.Container style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}>
                <Forms.Form onSubmit={e => onSubmitHandler(e)} noValidate={true}>
                    <Forms.Logo/>
                    <Forms.InputContainer>
                        <Forms.Input
                            ref={emailRef}
                            hasError={emailHasError || globalErrors}
                            name={'email'}
                            type={'email'}
                            placeholder={'Email'}
                            autoComplete={'email'}
                        />
                        <Forms.UserIconLeft hasError={emailHasError || globalErrors}/>
                    </Forms.InputContainer>
                    {emailHasError?.message && <Forms.ValidationError
                    >{emailHasError.message}</Forms.ValidationError>}

                    <Forms.InputContainer>
                        <Forms.Input
                            hasError={passwordHasError || globalErrors}
                            name={'password'}
                            type={state.showPassword ? 'text' : 'password'}
                            placeholder={'Password'}
                            autoComplete={'current-password'}
                        />
                        <Forms.PasswordIconLeft hasError={passwordHasError || globalErrors}/>
                        <Forms.PasswordIconRight
                            hasError={passwordHasError || globalErrors}
                            onClick={() => setState(prevState => ({ ...prevState, showPassword: !prevState.showPassword }))}
                        />
                    </Forms.InputContainer>
                    {passwordHasError && <Forms.ValidationError
                    >{passwordHasError.message}</Forms.ValidationError>}


                    {(globalErrors as any[])?.map((err: any, i: number) => (
                            <Forms.ValidationError key={`global-error-${i}`}>
                                {err?.message || err}
                            </Forms.ValidationError>
                        )
                    )}

                    <Forms.Button type='submit'>Sign in</Forms.Button>
                    {socialButtons?.length ? (
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="col-span-2 text-center align-center text-white">Or continue with</div>
                            <div className="grid grid-cols-3 gap-2">{socialButtons}</div>
                        </div>
                    ) : null}

                    <Forms.SpeechBubble>
                        <div className='speech-bubble'>
                            <p className='speech-title'><strong>Forgot your password? Don&apos;t have an account?</strong></p>
                            <p>Visit <strong>https://gameglass.gg/download</strong> on your PC to install <strong>GameGlass Hub</strong>.</p>
                        </div>
                    </Forms.SpeechBubble>
                </Forms.Form>
            </Forms.Container>
        )
}

export default Authentication
